/**
 * Application header/navigation bar.
 * Shows responsive navigation, user menu, and admin-only links.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useMediaQuery,
  Theme,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Class as ClassIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

/**
 * Top bar with navigation and account actions.
 */
const Header: React.FC = () => {
  const { isAuthenticated, user, logout, logoutAllSessions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  
  // User menu state
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isUserMenuOpen = Boolean(userMenuAnchorEl);
  
  // Navigation menu state (for mobile)
  const [navMenuAnchorEl, setNavMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isNavMenuOpen = Boolean(navMenuAnchorEl);
  
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseUserMenu = () => {
    setUserMenuAnchorEl(null);
  };
  
  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNavMenuAnchorEl(event.currentTarget);
  };
  
  const handleCloseNavMenu = () => {
    setNavMenuAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      handleCloseUserMenu();
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still navigate to login page even if there's an error
      handleCloseUserMenu();
      navigate('/login');
    }
  };
  
  const handleLogoutAllSessions = async () => {
    try {
      await logoutAllSessions();
      handleCloseUserMenu();
      navigate('/login');
    } catch (error) {
      console.error('Error during logout from all sessions:', error);
      // Still navigate to login page even if there's an error
      handleCloseUserMenu();
      navigate('/login');
    }
  };
  
  const navigateTo = (path: string) => {
    navigate(path);
    handleCloseNavMenu();
  };
  
  // Navigation items
  const navItems = [
    { label: 'API Status', path: '/api-status', icon: <DashboardIcon fontSize="small" /> },
    { label: 'School Year', path: '/select-school-year', icon: <SettingsIcon fontSize="small" /> },
    { label: 'Classes', path: '/class-selection', icon: <ClassIcon fontSize="small" /> },
    { label: 'DB Viewer', path: '/student-mappings', icon: <StorageIcon fontSize="small" /> }
  ];
  
  // Admin-only items
  const adminItems = [
    { label: 'User Management', path: '/user-management', icon: <GroupIcon fontSize="small" /> }
  ];
  
  // Don't show the header on the login page
  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <AppBar position="static">
      <Toolbar>
        {/* Mobile menu button */}
        {isMobile && (
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
            onClick={handleOpenNavMenu}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        {/* Logo/Title */}
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="div" 
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          onClick={() => navigate('/api-status')}
        >
          WELA-NeoLMS
        </Typography>
        
        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {navItems.map(item => (
              <Button
                key={item.path}
                color="inherit"
                startIcon={item.icon}
                onClick={() => navigateTo(item.path)}
                sx={{ 
                  mr: 1,
                  borderBottom: location.pathname === item.path ? '2px solid white' : 'none'
                }}
              >
                {item.label}
              </Button>
            ))}
            
            {/* Admin-only items */}
            {user?.is_admin && adminItems.map(item => (
              <Button
                key={item.path}
                color="inherit"
                startIcon={item.icon}
                onClick={() => navigateTo(item.path)}
                sx={{ 
                  mr: 1,
                  borderBottom: location.pathname === item.path ? '2px solid white' : 'none'
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}
        
        {/* Mobile Navigation Menu */}
        <Menu
          id="nav-menu"
          anchorEl={navMenuAnchorEl}
          open={isNavMenuOpen}
          onClose={handleCloseNavMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {/* Regular nav items */}
          {navItems.map(item => (
            <MenuItem 
              key={item.path} 
              onClick={() => navigateTo(item.path)}
              selected={location.pathname === item.path}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {item.icon}
                <Typography sx={{ ml: 1 }}>{item.label}</Typography>
              </Box>
            </MenuItem>
          ))}
          
          {/* Admin-only items - use array-based approach instead of fragments */}
          {user?.is_admin ? [
            <Divider key="admin-divider" />,
            ...adminItems.map(item => (
              <MenuItem 
                key={item.path} 
                onClick={() => navigateTo(item.path)}
                selected={location.pathname === item.path}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                  <Typography sx={{ ml: 1 }}>{item.label}</Typography>
                </Box>
              </MenuItem>
            ))
          ] : []}
        </Menu>
        
        {/* User Menu */}
        {isAuthenticated && user ? (
          <Box>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="account"
              onClick={handleOpenUserMenu}
            >
              <Badge
                color="secondary"
                variant="dot"
                invisible={!user.is_admin}
              >
                <AccountCircleIcon />
              </Badge>
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={userMenuAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={isUserMenuOpen}
              onClose={handleCloseUserMenu}
            >
              <MenuItem disabled>
                <Typography variant="body2">
                  {user.username} {user.is_admin && '(Admin)'}
                </Typography>
              </MenuItem>
              {[
                // User Management menu item (only for admins on mobile)
                ...(user.is_admin && isMobile 
                  ? [
                      <MenuItem key="user-management" onClick={() => navigateTo('/user-management')}>
                        User Management
                      </MenuItem>
                    ] 
                  : []
                ),
                // Divider
                <Divider key="divider" />,
                // Logout options
                <MenuItem key="logout" onClick={handleLogout}>Logout</MenuItem>,
                <MenuItem key="logout-all" onClick={handleLogoutAllSessions}>Logout from all devices</MenuItem>
              ]}
            </Menu>
          </Box>
        ) : null}
      </Toolbar>
    </AppBar>
  );
};

export default Header;